/** @format */

import { Environment, StoreInformation } from '@shared/data-access/interfaces';
/** @format */
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
	HttpService,
	MyAccountAnalyticsService,
	Store,
	StoreService
} from '@shared/data-access/services';

import { BatfittingSession } from '@my-account/data-access/interfaces';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';

@Injectable({
	providedIn: 'root'
})
export class BatfittingService {
	constructor(
		private httpService: HttpService,
		private store: Store,
		private myAccountAnalyticsService: MyAccountAnalyticsService,
		private storeService: StoreService,
		private environment: Environment
	) {}

	public getBatfittingSessions(): Observable<BatfittingSession[]> {
		const url = this.environment.baseUrls.batfittingService;

		const httpOptions = {
			headers: {
				'Content-Type': 'application/json'
			},
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.get(url, 0, httpOptions).pipe(
			map((result: BatfittingSession[]) => {
				const completedSessions = this.addStoreInfoToSessions(result);
				this.store.set('BATFITTING_SESSIONS', completedSessions);
				return completedSessions;
			}),
			catchError((err) => {
				this.myAccountAnalyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_BATFITTING,
					`Failed to get batfitting sessions.`,
					err
				);
				this.store.set('BATFITTING_SESSIONS', []);
				return throwError(err);
			})
		);
	}

	public getBatFittingSessionReport(sessionId: string) {
		const reportUrl = `${this.environment.baseUrls.batfittingService}${sessionId}/pdfReport`;

		const httpOptions = {
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/pdf'
			},
			responseType: 'blob' as const,
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.get(reportUrl, 0, httpOptions);
	}

	private addStoreInfoToSessions(sessions: BatfittingSession[]): BatfittingSession[] {
		const completedSessions: BatfittingSession[] = [];
		sessions.forEach((sessionWithoutStoreInfo) => {
			this.getStoreInfoForASession(
				sessionWithoutStoreInfo.storeId,
				sessionWithoutStoreInfo
			).subscribe({
				next: (sessionWithStoreInfo) => completedSessions.push(sessionWithStoreInfo),
				error: () => completedSessions.push(sessionWithoutStoreInfo)
			});
		});
		return completedSessions;
	}

	private getStoreInfoForASession(
		storeId: string,
		session: BatfittingSession
	): Observable<BatfittingSession> {
		return this.storeService.getBopisStoreInformation(storeId).pipe(
			map((storeInfo: StoreInformation) => {
				session.storeName = storeInfo.name;
				session.storeStreet = storeInfo.address1;
				session.storeCity = storeInfo.city;
				session.storeState = storeInfo.state;
				session.storeZip = storeInfo.zipcode;
				return session;
			}),
			catchError((err) => {
				this.myAccountAnalyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_BATFITTING,
					`Failed to get batfitting sessions store information.`,
					err
				);
				throw err;
			})
		);
	}
}
